import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import VehicleInspectionProductsList from '../components/VehicleInspectionProductsList'
import { FormattedMessage } from 'react-intl'
import { Copy, Button, Flex, Box, HorizontalRule, Hide, DotsLoader } from '@elparking/components'

import ButtonWithPrice from '../components/ButtonWithPrice'
import VehicleInspectionProductsModal from '../components/VehicleInspectionProductsModal'

import VehicleFilterSelectorContainer from '../components/VehicleFilterSelectorContainer'
import VehicleInspectionProductSelectorHeader from '../components/VehicleInspectionProductSelectorHeader'
import Warning from '../ui/Warning'

import ResumeBlock from '../components/ResumeBlock'
import ScrollObserver from 'commons/js/util/ScrollObserver'
import { useApi } from '@elparking/utils'
import { getProductWithLowerPrice, sortByPrice } from '../util/vehicleInspectionProductsPrices'
import { getProductByVehicleAndStation, navigateToBookingProcess } from '../services/vehicleInspectionProducts'
import VehicleInpectionSelectorMessage, { mapMessages as availableFilters } from 'commons/js/ui/VehicleInpectionSelectorMessage.js'
import { toUrlSearchParams } from 'commons/js/util/UrlTo'

import { Events, trackEvent } from 'commons/js/services/analytics'

function onScrollClick (observableElement) {
    observableElement && observableElement.scrollIntoView()
}

function getLabel (filter) {
    return filter && availableFilters[filter] && <VehicleInpectionSelectorMessage value={filter} />
}

const hasElements = (products) => products && products.length > 0
const isVehicleFilterDefined = (vehicleFilter) => vehicleFilter.value !== null && vehicleFilter.value !== undefined

function VehicleInspectionProductSelectorContainer ({
        vehicleInspectionStation,
        defaultProduct,
        initialVehicleFilter,
        getProductByVehicleAndStation,
        navigateToBookingProcess,
        location,
        trackEvent,
        push,
    }) {
    const [vehicleFilter, setVehicleFilter] = useState({value: initialVehicleFilter, label: getLabel(initialVehicleFilter)})
    const [isOpen, setModal] = useState(false)
    const [observableElement, setObservableElement] = useState()
    const [selectedProduct, setSelectedProduct] = useState(null)
    const {
        response: {result: products},
        loading: loadingProducts,
        sendRequest: requestProducts,
    } = useApi(getProductByVehicleAndStation)

    useEffect(() => {
        const locationParams = new URLSearchParams(location.search).toString()
        const nextParams = toUrlSearchParams({vehicle_filter: vehicleFilter.value}).toString()
        const shouldUpdateUrl = (locationParams !== nextParams) && isVehicleFilterDefined(vehicleFilter)
        if (shouldUpdateUrl) {
            push({ search: nextParams })
        }
    }, [location, vehicleFilter, push])

    useEffect(() => {
        setSelectedProduct(getProductWithLowerPrice(products))
    }, [products, setSelectedProduct])

    useEffect(() => {
        vehicleFilter.value && vehicleFilter.label && requestProducts({vehicleFilter: vehicleFilter.value, stationId: vehicleInspectionStation.id})
    }, [requestProducts, vehicleFilter, vehicleInspectionStation])

    return (
        <Box pb='1em' ref={setObservableElement}>
            { !isVehicleFilterDefined(vehicleFilter) && <VehicleInspectionProductSelectorHeader product={defaultProduct} /> }
            <Box pt='12px'>
                <VehicleFilterSelectorContainer vehicleFilter={vehicleFilter.value} setVehicleFilter={setVehicleFilter} />
            </Box>
            { isVehicleFilterDefined(vehicleFilter) && <Hide xs sm md><HorizontalRule /></Hide>}
            <Box px='12px'>
                {
                (isVehicleFilterDefined(vehicleFilter) && loadingProducts)
                ? <Flex height={['auto', '120px']} alignItems='center' justifyContent='center' padding={['0', '16px']}>
                    <DotsLoader />
                </Flex>
                : <>
                    <Hide xs sm md>
                        {
                            isVehicleFilterDefined(vehicleFilter) &&
                            <Copy padding='0 0 12px 0' fontSize='xxxSmall' fontWeigth='book' transform='uppercase' color='gray'>
                                <FormattedMessage defaultMessage='Selecciona la opción que mejor se adapta a ti' />
                            </Copy>
                        }
                        {
                            isVehicleFilterDefined(vehicleFilter) && hasElements(products) &&
                            <>
                                <Flex as='form' flexWrap='wrap' alignItems='stretch' onSubmit={() => {}} noValidate>
                                    <Box flex='1 0 100%' mb='1em' style={{ minWidth: 0 }}>
                                        <VehicleInspectionProductsList products={sortByPrice(products)} selectedProduct={selectedProduct} onClick={setSelectedProduct}/>
                                    </Box>
                                </Flex>
                                <Button
                                  dataTest='itv-detail-continue-button'
                                  width='100%'
                                  onClick={() => {
                                        trackEvent(Events.vehicleInspectionStationCheckOut)
                                        navigateToBookingProcess(selectedProduct, vehicleFilter)
                                    }}
                                >
                                    <FormattedMessage defaultMessage='Continuar' />
                                </Button>
                            </>
                        }
                    </Hide>
                    <Hide lg>
                        <VehicleInspectionProductsModal
                          isOpen={isOpen}
                          onClose={() => setModal(false)}
                          onClick={setSelectedProduct}
                          onSubmit={() => {
                              trackEvent(Events.vehicleInspectionStationCheckOut)
                              navigateToBookingProcess(selectedProduct, vehicleFilter)
                          }}
                          products={products}
                          selectedProduct={selectedProduct}
                        />
                        {
                            isVehicleFilterDefined(vehicleFilter) && hasElements(products) && <ButtonWithPrice onClick={() => setModal(true)} originalPrice={selectedProduct && selectedProduct.originalPrice} totalPrice={selectedProduct && selectedProduct.totalPrice}/>
                        }
                    </Hide>
                    {
                        !isVehicleFilterDefined(vehicleFilter) && <Button marginTop={['0px', '4px']} width='100%' backgroundColor='gray' disabled>
                            <FormattedMessage defaultMessage='Selecciona el tipo de vehículo' />
                        </Button>
                    }
                </>
                }
                {
                    isVehicleFilterDefined(vehicleFilter) && !hasElements(products) && !loadingProducts &&
                    <Box>
                        <Warning>
                            <FormattedMessage defaultMessage='No hay opciones disponibles para este tipo de vehículo' />
                        </Warning>
                    </Box>
                }
                {
                    <ScrollObserver observableElement={observableElement}>
                        <Hide lg>
                            <ResumeBlock
                              vehicleFilter={vehicleFilter.label}
                              vehicleInspectionProduct={defaultProduct}
                              vehicleInspectionStationName={vehicleInspectionStation.name}
                              onClick={() => onScrollClick(observableElement)}
                            />
                        </Hide>
                    </ScrollObserver>
                }
            </Box>
        </Box>
    )
}

VehicleInspectionProductSelectorContainer.defaultProps = {
    getProductByVehicleAndStation: getProductByVehicleAndStation,
    trackEvent,
    navigateToBookingProcess,
}

VehicleInspectionProductSelectorContainer.propTypes = {
    vehicleInspectionStation: PropTypes.object,
    initialVehicleFilter: PropTypes.string,
    navigateToBookingProcess: PropTypes.func,
    getProductByVehicleAndStation: PropTypes.func,
    location: PropTypes.object,
    push: PropTypes.func,
    trackEvent: PropTypes.func,
    defaultProduct: PropTypes.object,
}

export default VehicleInspectionProductSelectorContainer
